import type { NavigationGuard } from 'vue-router'
import { useAccountStore } from '@/stores/account.ts'
import { routeNames } from '@/router/names.ts'
import { getAccessToken } from '@/helpers/auth.ts'
import { useSubAccountStore } from '@/stores/subAccount.ts'
import { routeNamesByDocumentCode } from '@/config/requiredDocumentsCodes.ts'
import {
    findAssignedSubAccount,
    getAssignedSubAccountIds,
    requiresCurrentTermsAcceptance,
} from '@/helpers/guardianTerms.ts'

export const guestGuard: NavigationGuard = async (to, from, next) => {
    const accountStore = useAccountStore()
    const subAccountStore = useSubAccountStore()

    const token = getAccessToken()

    if (!token) {
        accountStore.logout(true)

        return next()
    }

    if (accountStore.accountData && subAccountStore.subAccountsOptions.length) {
        if (!accountStore.accountData.childProtectAcceptedAt) {
            return next({
                name: routeNames.CHILD_PROTECTION_VIEW_NAME,
            })
        }

        const targetSubAccountId =
            getAssignedSubAccountIds(accountStore.accountData)[0] ||
            subAccountStore.subAccountsOptions[0]!.subaccountId
        const targetSubAccount = findAssignedSubAccount(
            accountStore.accountData,
            targetSubAccountId,
        )

        if (requiresCurrentTermsAcceptance(targetSubAccount)) {
            return next({
                name: routeNames.GUARDIAN_TERMS_VIEW_NAME,
                params: {
                    subAccountId: targetSubAccountId,
                },
            })
        }

        return next({
            name: routeNames.HOME_VIEW_NAME,
            params: {
                subAccountId: targetSubAccountId,
            },
        })
    }

    const { isSuccess: isChildProtectStatusLoadSuccess, childProtectAccepted } =
        await accountStore.loadChildProtectAcceptedStatus()

    if (!isChildProtectStatusLoadSuccess) {
        accountStore.logout(true)

        return next()
    }

    if (!childProtectAccepted) {
        return next({
            name: routeNames.CHILD_PROTECTION_VIEW_NAME,
        })
    }

    const { isSuccess: isAccountDataLoadSuccess, documentToAccept: documentToAccept1 } =
        await accountStore.loadAccountData()

    if (documentToAccept1) {
        return next({
            name: routeNamesByDocumentCode[documentToAccept1],
        })
    }

    if (isAccountDataLoadSuccess) {
        const assignedSubAccountIds = getAssignedSubAccountIds(accountStore.accountData)
        const targetSubAccountId = assignedSubAccountIds[0]
        const targetSubAccount = findAssignedSubAccount(
            accountStore.accountData,
            targetSubAccountId,
        )

        if (requiresCurrentTermsAcceptance(targetSubAccount)) {
            return next({
                name: routeNames.GUARDIAN_TERMS_VIEW_NAME,
                params: {
                    subAccountId: targetSubAccountId,
                },
            })
        }

        subAccountStore.setSubAccountsOptionsFromAccountData(accountStore.accountData)

        return next({
            name: routeNames.HOME_VIEW_NAME,
            params: {
                subAccountId: targetSubAccountId,
            },
        })
    }

    accountStore.logout(true)

    return next()
}
